"use client";

import {
	calendarDaysApart,
	dateLabel,
	Time,
	useNow,
	useTimeZone,
} from "@commerce/components/time";
import {
	DetailUnavailable,
	NoMatches,
	PaneEmpty,
} from "@commerce/conversations/EmptyStates";
import { orpc, orpcClient } from "@shared/lib/orpc";
import { useDebouncedValue } from "@tanstack/react-pacer";
import {
	useQuery,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { Badge } from "@ui/components/badge";
import { Button } from "@ui/components/button";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput,
} from "@ui/components/input-group";
import { Skeleton } from "@ui/components/skeleton";
import { Spinner } from "@ui/components/spinner";
import { toast } from "@ui/components/toast";
import { cn } from "@ui/lib";
import {
	PhoneIcon,
	PhoneIncomingIcon,
	PhoneMissedIcon,
	SearchIcon,
	XIcon,
} from "lucide-react";
import {
	useCallback,
	useDeferredValue,
	useEffect,
	useMemo,
	useState,
} from "react";
import { CallDetail, CallDetailSkeleton } from "./CallDetail";
import {
	CALL_FILTERS,
	CALL_STATUS_META,
	type CallFilter,
	type CallsSearch,
	callerLabel,
	clockDuration,
	dayGroup,
	FILTER_LABEL,
	isLive,
	listInputFrom,
} from "./meta";
import type { CallList, CallRow } from "./types";

/** Fallback only: the org layout's live subscription invalidates on push. */
const HEARTBEAT_POLL_MS = 60_000;
/** While a call is live its timer and captions move; the live list refreshes faster. */
const LIVE_POLL_MS = 5_000;

const route = getRouteApi("/_saas/app/_org/$organizationSlug/calls/");

/**
 * Calls: [list grouped Live / Today / Yesterday…] [one call], like the Inbox (spec 5.4).
 * Below lg the list and the call are one pane each, with a back button.
 */
export function CallsScreen({ organizationId }: { organizationId: string }) {
	const { organizationSlug } = route.useParams();
	const search = route.useSearch();
	const navigate = route.useNavigate();
	const queryClient = useQueryClient();

	const setSearch = useCallback(
		(patch: Partial<CallsSearch>) =>
			navigate({
				search: (prev) => ({ ...prev, ...patch }),
				replace: true,
			}),
		[navigate],
	);

	const [searchText, setSearchText] = useState(search.q ?? "");
	const [debouncedText] = useDebouncedValue(searchText.trim(), { wait: 250 });
	useEffect(() => {
		if ((search.q ?? "") !== debouncedText) {
			setSearch({ q: debouncedText || undefined });
		}
	}, [debouncedText, search.q, setSearch]);

	const listInput = useMemo(
		() => listInputFrom(organizationId, search),
		[organizationId, search],
	);
	const deferredInput = useDeferredValue(listInput);
	const { data: list, isFetching } = useSuspenseQuery({
		...orpc.calls.list.queryOptions({ input: deferredInput }),
		refetchInterval: HEARTBEAT_POLL_MS,
	});
	const { data: liveData } = useSuspenseQuery({
		...orpc.calls.live.queryOptions({ input: { organizationId } }),
		refetchInterval: (query) =>
			(query.state.data?.calls.length ?? 0) > 0
				? LIVE_POLL_MS
				: HEARTBEAT_POLL_MS,
	});

	const selectedId = search.id ?? null;
	const [loadingMore, setLoadingMore] = useState(false);
	const loadMore = async () => {
		if (!list.nextCursor || loadingMore) {
			return;
		}
		setLoadingMore(true);
		try {
			const next = await orpcClient.calls.list({
				...deferredInput,
				cursor: list.nextCursor,
			});
			queryClient.setQueryData(
				orpc.calls.list.queryKey({ input: deferredInput }),
				(old: CallList | undefined) => {
					if (!old) {
						return next;
					}
					const seen = new Set(old.calls.map((c) => c.id));
					return {
						...next,
						calls: [
							...old.calls,
							...next.calls.filter((c) => !seen.has(c.id)),
						],
					};
				},
			);
		} catch {
			toast.add({
				type: "error",
				title: "Couldn't load older calls",
				description: "Check your connection and try again.",
			});
		} finally {
			setLoadingMore(false);
		}
	};

	const select = useCallback(
		(id: string | null) =>
			setSearch({ id: id ?? undefined, tab: undefined }),
		[setSearch],
	);

	const filtered = Boolean(search.show || search.q);
	// A live call is shown once, under Live; the history skips it until it ends.
	const liveIds = new Set(liveData.calls.map((c) => c.id));
	const history = list.calls.filter((c) => !liveIds.has(c.id));
	const noCalls = list.calls.length + liveData.calls.length === 0;
	const live = filtered ? [] : liveData.calls;

	return (
		<div className="flex min-h-0 flex-1 flex-col">
			<CallsHeader
				hidden={Boolean(selectedId)}
				liveCount={liveData.calls.length}
				isFetching={isFetching}
				searchText={searchText}
				onSearchTextChange={setSearchText}
				show={search.show}
				onShowChange={(show) => setSearch({ show, id: undefined })}
			/>

			<div className="flex min-h-0 flex-1">
				<aside
					aria-label="Calls"
					className={cn(
						"min-h-0 min-w-0 flex-col border-border/70 lg:flex lg:w-[22rem] lg:shrink-0 lg:border-r xl:w-96",
						selectedId ? "hidden" : "flex flex-1 lg:flex-none",
						// No calls at all: one empty state across the page, not one per pane.
						noCalls &&
							"lg:w-auto lg:flex-1 lg:border-r-0 xl:w-auto",
					)}
				>
					<GroupedCalls
						live={live}
						calls={history}
						selectedId={selectedId}
						onSelect={select}
						hasMore={Boolean(list.nextCursor)}
						loadingMore={loadingMore}
						onLoadMore={loadMore}
						filtered={filtered}
						onClearFilters={() => {
							setSearchText("");
							setSearch({ q: undefined, show: undefined });
						}}
					/>
				</aside>
				<section
					aria-label="Call"
					className={cn(
						"min-h-0 min-w-0 flex-1 flex-col lg:flex",
						selectedId ? "flex" : "hidden",
						noCalls && !selectedId && "lg:hidden",
					)}
				>
					{selectedId ? (
						<SelectedCall
							key={selectedId}
							callId={selectedId}
							organizationId={organizationId}
							organizationSlug={organizationSlug}
							onBack={() => select(null)}
						/>
					) : (
						<PaneEmpty
							icon={<PhoneIcon />}
							title="Pick a call"
							description="Its recording, transcript and what was sent to the chat open here."
						/>
					)}
				</section>
			</div>
		</div>
	);
}

/** Title, live count, search and the status filter above both panes. */
function CallsHeader({
	hidden,
	liveCount,
	isFetching,
	searchText,
	onSearchTextChange,
	show,
	onShowChange,
}: {
	/** A call is open: below lg the header gives its room to the call. */
	hidden: boolean;
	liveCount: number;
	isFetching: boolean;
	searchText: string;
	onSearchTextChange: (text: string) => void;
	show: CallFilter | undefined;
	onShowChange: (show: CallFilter | undefined) => void;
}) {
	return (
		<div
			className={cn(
				"flex shrink-0 flex-col gap-3 border-border/70 border-b px-4 pt-4 pb-3 md:px-6",
				hidden && "max-lg:hidden",
			)}
		>
			<div className="flex flex-wrap items-center gap-x-3 gap-y-2">
				<h1 className="font-semibold text-xl tracking-tight">Calls</h1>
				{liveCount > 0 ? (
					<Badge variant="success">
						<span
							aria-hidden
							className="size-1.5 rounded-full bg-current"
						/>
						{liveCount} live
					</Badge>
				) : null}
				<InputGroup className="min-w-0 flex-1 sm:ml-auto sm:w-64 sm:flex-none">
					<InputGroupAddon>
						{isFetching ? <Spinner /> : <SearchIcon aria-hidden />}
					</InputGroupAddon>
					<InputGroupInput
						value={searchText}
						onChange={(e) => onSearchTextChange(e.target.value)}
						placeholder="Caller name or number"
						aria-label="Search calls"
						dir="auto"
					/>
					{searchText ? (
						<InputGroupAddon align="inline-end">
							<InputGroupButton
								size="icon-xs"
								onClick={() => onSearchTextChange("")}
								aria-label="Clear search"
							>
								<XIcon />
							</InputGroupButton>
						</InputGroupAddon>
					) : null}
				</InputGroup>
			</div>
			<fieldset className="no-scrollbar flex min-w-0 gap-1 overflow-x-auto">
				<legend className="sr-only">Show calls</legend>
				{([undefined, ...CALL_FILTERS] as const).map((value) => {
					const active = show === value;
					return (
						<button
							key={value ?? "all"}
							type="button"
							aria-pressed={active}
							onClick={() => onShowChange(value)}
							className={cn(
								"inline-flex h-7 shrink-0 items-center rounded-xl px-2.5 font-medium text-sm outline-none transition-colors focus-visible:ring-3 focus-visible:ring-ring/30",
								active
									? "bg-foreground text-background"
									: "text-muted-foreground hover:bg-muted hover:text-foreground",
							)}
						>
							{value ? FILTER_LABEL[value] : "All"}
						</button>
					);
				})}
			</fieldset>
		</div>
	);
}

/** The open call: its detail, a retry when it failed to load, a skeleton meanwhile. */
function SelectedCall({
	callId,
	organizationId,
	organizationSlug,
	onBack,
}: {
	callId: string;
	organizationId: string;
	organizationSlug: string;
	onBack: () => void;
}) {
	const detailQuery = useQuery({
		...orpc.calls.get.queryOptions({ input: { organizationId, callId } }),
		refetchInterval: (query) =>
			query.state.data && isLive(query.state.data.status)
				? LIVE_POLL_MS
				: false,
	});
	if (detailQuery.data) {
		return (
			<CallDetail
				call={detailQuery.data}
				organizationId={organizationId}
				organizationSlug={organizationSlug}
				onBack={onBack}
			/>
		);
	}
	if (detailQuery.isError) {
		return (
			<DetailUnavailable
				title="Couldn't open this call"
				backLabel="Back to calls"
				onBack={onBack}
				onRetry={() => detailQuery.refetch()}
			/>
		);
	}
	return <CallDetailSkeleton />;
}

function GroupedCalls({
	live,
	calls,
	selectedId,
	onSelect,
	hasMore,
	loadingMore,
	onLoadMore,
	filtered,
	onClearFilters,
}: {
	live: readonly CallRow[];
	calls: readonly CallRow[];
	selectedId: string | null;
	onSelect: (id: string) => void;
	hasMore: boolean;
	loadingMore: boolean;
	onLoadMore: () => void;
	filtered: boolean;
	onClearFilters: () => void;
}) {
	const timeZone = useTimeZone();
	const nowMs = useNow();
	// Server and first client render group from the newest call, so they agree; the real
	// "now" takes over once the page is live.
	const groups = useMemo(() => {
		const now = nowMs
			? new Date(nowMs)
			: new Date(calls[0]?.ringingAt ?? Date.now());
		const result: Array<{ label: string; calls: CallRow[] }> = [];
		for (const call of calls) {
			const at = new Date(call.ringingAt);
			const label = dayGroup(
				calendarDaysApart(at, now, timeZone),
				dateLabel(at, timeZone, false),
			);
			const last = result.at(-1);
			if (last?.label === label) {
				last.calls.push(call);
			} else {
				result.push({ label, calls: [call] });
			}
		}
		return result;
	}, [calls, nowMs, timeZone]);

	if (live.length === 0 && calls.length === 0) {
		return filtered ? (
			<NoMatches
				title="No calls match"
				clearLabel="Show all calls"
				onClear={onClearFilters}
			/>
		) : (
			<PaneEmpty
				icon={<PhoneIcon />}
				title="No calls yet"
				description="Turn on calls for a number in Channels, on its Calling tab."
			/>
		);
	}

	return (
		<div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-2">
			{live.length > 0 ? (
				<CallGroup
					label="Live"
					calls={live}
					selectedId={selectedId}
					onSelect={onSelect}
				/>
			) : null}
			{groups.map((group) => (
				<CallGroup
					key={group.label}
					label={group.label}
					calls={group.calls}
					selectedId={selectedId}
					onSelect={onSelect}
				/>
			))}
			{hasMore ? (
				<div className="px-1 pt-2 pb-1">
					<Button
						variant="ghost"
						size="sm"
						onClick={onLoadMore}
						disabled={loadingMore}
						className="w-full text-muted-foreground"
					>
						{loadingMore ? (
							<Spinner data-icon="inline-start" />
						) : null}
						Load older calls
					</Button>
				</div>
			) : null}
		</div>
	);
}

function CallGroup({
	label,
	calls,
	selectedId,
	onSelect,
}: {
	label: string;
	calls: readonly CallRow[];
	selectedId: string | null;
	onSelect: (id: string) => void;
}) {
	return (
		<section aria-label={label} className="flex flex-col gap-0.5 pb-2">
			<h2 className="px-3 pt-2 pb-1 font-medium text-muted-foreground text-xs">
				{label}
			</h2>
			<ul className="flex flex-col gap-0.5">
				{calls.map((call) => (
					<li key={call.id}>
						<CallRowButton
							call={call}
							selected={call.id === selectedId}
							onSelect={onSelect}
						/>
					</li>
				))}
			</ul>
		</section>
	);
}

function CallRowButton({
	call,
	selected,
	onSelect,
}: {
	call: CallRow;
	selected: boolean;
	onSelect: (id: string) => void;
}) {
	const live = isLive(call.status);
	const missed = call.status === "MISSED" || call.status === "REJECTED";
	const Icon = missed ? PhoneMissedIcon : PhoneIncomingIcon;
	const status = CALL_STATUS_META[call.status];
	return (
		<button
			type="button"
			data-call-id={call.id}
			aria-current={selected ? "true" : undefined}
			onClick={() => onSelect(call.id)}
			className={cn(
				"flex w-full items-start gap-3 rounded-2xl px-3 py-2.5 text-left outline-none transition-colors duration-(--duration-hover) focus-visible:ring-3 focus-visible:ring-ring/30",
				selected ? "bg-muted" : "hover:bg-muted/60",
			)}
		>
			<Icon
				aria-hidden
				className={cn(
					"mt-0.5 size-4 shrink-0",
					missed
						? "text-warning-foreground dark:text-warning"
						: "text-muted-foreground",
				)}
			/>
			<span className="flex min-w-0 flex-1 flex-col gap-1">
				<span className="flex items-center gap-2">
					<span
						dir="auto"
						className="min-w-0 flex-1 truncate font-medium text-sm"
					>
						{callerLabel(call)}
					</span>
					{live ? (
						<LiveTimer since={call.answeredAt ?? call.ringingAt} />
					) : (
						<Time
							at={new Date(call.ringingAt)}
							label="Rang"
							className="shrink-0 text-muted-foreground text-xs tabular"
						/>
					)}
				</span>
				<span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-muted-foreground text-xs">
					{call.status !== "ENDED" ? (
						<Badge variant={status.variant}>{status.label}</Badge>
					) : null}
					{call.durationSeconds !== null && !live ? (
						<span className="tabular">
							{clockDuration(call.durationSeconds)}
						</span>
					) : null}
					{call.summary ? (
						<span
							dir="auto"
							className="line-clamp-1 min-w-0 flex-1 text-left"
						>
							{call.summary}
						</span>
					) : null}
				</span>
			</span>
		</button>
	);
}

function LiveTimer({ since }: { since: Date | string }) {
	const now = useNow();
	const [tick, setTick] = useState(0);
	useEffect(() => {
		const timer = window.setInterval(() => setTick((t) => t + 1), 1000);
		return () => window.clearInterval(timer);
	}, []);
	if (!now) {
		return null;
	}
	const seconds = (Date.now() - new Date(since).getTime()) / 1000;
	return (
		<span
			className="shrink-0 font-medium text-success text-xs tabular"
			data-tick={tick}
		>
			{clockDuration(seconds)}
		</span>
	);
}

export function CallsSkeleton() {
	return (
		<div className="flex min-h-0 flex-1 flex-col">
			<div className="flex flex-col gap-3 border-border/70 border-b px-4 pt-4 pb-3 md:px-6">
				<div className="flex items-center justify-between gap-3">
					<Skeleton className="h-7 w-20" />
					<Skeleton className="h-8 w-64 max-sm:hidden" />
				</div>
				<div className="flex gap-1">
					{[10, 22, 18, 16].map((w) => (
						<Skeleton
							key={w}
							className="h-7"
							style={{ width: `${w * 4}px` }}
						/>
					))}
				</div>
			</div>
			<div className="flex min-h-0 flex-1">
				<div className="flex w-full flex-col gap-1 p-2 lg:w-[22rem] lg:border-border/70 lg:border-r xl:w-96">
					<Skeleton className="mx-3 mt-2 mb-1 h-3 w-12" />
					{Array.from({ length: 6 }).map((_, i) => (
						<div key={i} className="flex gap-3 px-3 py-2.5">
							<Skeleton className="size-4" />
							<div className="flex flex-1 flex-col gap-2">
								<Skeleton className="h-4 w-2/3" />
								<Skeleton className="h-3 w-1/3" />
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}
